import React, {
    useState, useEffect, useCallback, useContext
} from 'react';
import _ from 'lodash';
import { withRouter, useHistory } from 'react-router-dom';
import {
    Loader, FormattedDate, useModal
} from '@jutro/components';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { useAuthentication } from 'gw-digital-auth-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import metadata from './NewServiceCreateComponent_AX.metadata.json5';
import styles from './NewServiceCreateComponent_AX.module.scss';
import messages from './NewServiceCreateComponent_AX.messages';
import AddressComponentAX from '../AddressNewServiceComponent_AX/AddressNewServiceComponent_AX';

function NewServiceCreateComponentAX(props) {
    const { isComponentValid, onValidate } = useValidation('createService');
    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);

    const {
        claimSelected,
        serviceRequestVM,
        onUpdateModelSRVM,
        serviceRequestKind,
        addressVM,
        onUpdateModelAddressVM,
        exposuresClaim,
        updateExposuresClaim,
        servicesExposure,
        updateServicesExposure
    } = props;

    const [isLoading, setLoadingState] = useState(false);

    const [exposuresList, setExposuresList] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [servicesKindList, setServicesKindList] = useState([]);
    const [addressList, setAddressList] = useState([]);

    const [isNewAddress, setIsNewAddress] = useState(false);
    const history = useHistory();
    const {
        showAlert,
        showConfirm
    } = useModal();
    const createExposuresList = useCallback((exposures) => {
        updateExposuresClaim(exposures);
        const arrayExposures = [];
        exposures.forEach((exposure) => {
            arrayExposures.push({
                displayName: exposure.displayName,
                id: exposure.exposureID
            });
        });
        setExposuresList(arrayExposures);

    // eslint-disable-next-line no-use-before-define
    }, [updateExposuresClaim]);

    const getExposures = useCallback(() => {
        ServiceRequestService.findAvailableServiceRequestForClaim(
            claimSelected.claimNumber,
            authHeader
        ).then((exposures) => {
            createExposuresList(exposures);
        });
    }, [authHeader, claimSelected, createExposuresList]);

    const getAddressLis = useCallback(() => {
        const arrayAddress = [];

        claimSelected.addresses.forEach((address) => {
            arrayAddress.push({
                displayName: address.displayName,
                id: address.publicID
            });
        });

        arrayAddress.push({
            displayName: translator(messages.enterNewAddress),
            id: 'new'
        });

        setAddressList(arrayAddress);
    }, [claimSelected, translator]);

    const setServicesSelectExposure = useCallback((exposureID) => {
        const servicesListExposure = _.filter(exposuresClaim,
            (item) => item.exposureID === exposureID)[0].services;

        const arrayServices = [];
        servicesListExposure.forEach((service) => {
            arrayServices.push({
                displayName: service.displayName,
                id: service.code
            });
        });

        updateServicesExposure(servicesListExposure);
        setServicesList(arrayServices);
        refreshData();
    }, [exposuresClaim, refreshData, updateServicesExposure]);

    const setServicesKindSelectExposure = useCallback((serviceCode) => {
        const availableValues = [];
        const serviceSelected = servicesExposure.filter((item) => item.code === serviceCode)[0];

        const arrayServicesKindList = serviceRequestKind.filter(
            (item) => serviceSelected.kinds.includes(item.code)
        );

        arrayServicesKindList.forEach((typecode) => {
            availableValues.push({
                id: typecode.code,
                displayName: translator({ id: typecode.name, defaultMessage: 'TBD' })
            });
        });

        setServicesKindList(availableValues);
    }, [serviceRequestKind, servicesExposure, translator]);

    useEffect(() => {
        getExposures();
        getAddressLis();

        if (_.get(serviceRequestVM, 'code.value') !== undefined) {
            setServicesSelectExposure(_.get(serviceRequestVM, 'exposureID.value.id'));
        }

        if (_.get(serviceRequestVM, 'kind.value') !== undefined) {
            setServicesKindSelectExposure(_.get(serviceRequestVM, 'code.value.id'));
        }

        if (_.get(serviceRequestVM, 'existingAddressID.value.id') !== undefined
            && _.get(serviceRequestVM, 'existingAddressID.value.id') === 'new') {
            setIsNewAddress(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExposureItem = useCallback((code) => {
        if (code !== undefined) {
            _.set(serviceRequestVM, 'exposureID', code);
            setServicesSelectExposure(code.id);
        }
        refreshData();
    }, [serviceRequestVM, setServicesSelectExposure, refreshData]);

    const handleServiceItem = useCallback((code) => {
        if (code !== undefined) {
            _.set(serviceRequestVM, 'code', code);
            setServicesKindSelectExposure(code.id);
        }
        refreshData();
    }, [serviceRequestVM, setServicesKindSelectExposure, refreshData]);

    const handleKindServiceItem = useCallback((code) => {
        let kindService;
        if (code !== undefined) {
            // eslint-disable-next-line prefer-destructuring
            kindService = serviceRequestKind.filter((item) => item.code === code.id)[0];
            _.set(serviceRequestVM, 'kind', kindService);
        }
        refreshData();
    }, [serviceRequestKind, serviceRequestVM, refreshData]);

    const handleAddressItem = useCallback((code) => {
        if (code !== undefined) {
            _.set(serviceRequestVM, 'existingAddressID', code);
            setIsNewAddress(code.id === 'new');
        }
        refreshData();
    }, [serviceRequestVM, refreshData]);

    const handleModelChange = useCallback((newModel) => {
        onUpdateModelSRVM(newModel);
        refreshData();
    }, [onUpdateModelSRVM, refreshData]);

    const getDateModified = useCallback((value) => {
        if (typeof value === 'undefined') {
            return '';
        }
        return <FormattedDate value={new Date(value)} format="short" />;
    }, []);

    const validateDataSRVM = (serviceRequestData, newAddressData) => {
        let isDataValid = false;

        if (serviceRequestData.kind !== undefined
            && serviceRequestData.exposureID !== undefined
            && serviceRequestData.existingAddressID !== undefined
            && serviceRequestData.expectedCompletionDate !== undefined
            && serviceRequestData.comments !== undefined
            && serviceRequestData.comments.trim() !== '') {
            if (serviceRequestData.existingAddressID.id === 'new'
                && newAddressData.city !== undefined) {
                isDataValid = true;
            }
            if (serviceRequestData.existingAddressID.id !== 'new') {
                isDataValid = true;
            }
        }
        return isDataValid;
    };

    const callCreateService = (serviceRequestData, newAddressData, auth) => {
        setLoadingState(true);

        if (newAddressData.city === undefined) {
            newAddressData.city = '';
        }

        ServiceRequestService.createNewServiceRequestAX(
            serviceRequestData,
            newAddressData,
            auth
        ).then((result) => {
            showAlert({
                status: 'success',
                icon: 'mi-info',
                title: translator(messages.createServiceSuccess),
                message: translator(messages.createServiceSuccessMessage, {
                    serviceRequestNumber: result
                })
            }).then(() => {
                history.push('/');
            }).catch(() => {
                history.push('/');
            });
        }).catch(() => {
            showAlert({
                status: 'error',
                icon: 'mi-error',
                title: translator(messages.createServiceError),
                message: translator(messages.createServiceErrorMessage)
            });
        }).finally(() => {
            setLoadingState(false);
        });
    };

    const createService = useCallback(() => {
        if (validateDataSRVM(serviceRequestVM.value, addressVM.value)) {
            const addressSelected = _.get(serviceRequestVM, 'existingAddressID.value');

            let newAddress = {};
            const srDTO = _.clone(serviceRequestVM.value);
            srDTO.claimID = _.get(claimSelected, 'publicID');
            srDTO.exposureID = _.get(serviceRequestVM, 'exposureID.value.id');
            srDTO.code = _.get(serviceRequestVM, 'code.value.id');
            srDTO.existingAddressID = _.get(serviceRequestVM, 'existingAddressID.value.id');
            srDTO.expectedCompletionString = `${srDTO.expectedCompletionDate}T05:00:00.000Z`;

            if (addressSelected.id !== 'new') {
                const newAddressData = claimSelected.addresses.filter(
                    (addressElement) => addressElement.publicID === addressSelected.id
                )[0];
                newAddress = newAddressData;
            } else {
                newAddress = addressVM.value;
            }

            showConfirm({
                title: messages.createService,
                message: translator(messages.confirmCreateService, {
                    claimNumber: _.get(claimSelected, 'claimNumber')
                }),
                status: 'warning',
                icon: 'mi-help',
                confirmButtonText: messages.cancelConfirmYes,
                cancelButtonText: messages.cancelConfirmNo
            }).then(async (result) => {
                if (result === 'confirm') {
                    callCreateService(srDTO, newAddress, authHeader);
                }
            });
        } else {
            showAlert({
                status: 'error',
                icon: 'mi-error',
                title: translator(messages.errorValidate),
                message: translator(messages.errorMessageValidate)
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceRequestVM, addressVM, claimSelected, translator, authHeader]);

    const cancelService = useCallback(() => {
        showConfirm({
            title: messages.cancelService,
            message: translator(messages.confirmCancelService),
            status: 'warning',
            icon: 'mi-help',
            confirmButtonText: messages.cancelConfirmYes,
            cancelButtonText: messages.cancelConfirmNo
        }).then((result) => {
            if (result === 'confirm') {
                history.push('/');
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrides = {
        claimData: {
            labelPosition: 'top'
        },
        lossDateID: {
            value: getDateModified(_.get(claimSelected, 'lossDate'))
        },
        expectedCompletionDateID: {
            minDate: new Date()
        },
        exposuresDropdown: {
            availableValues: exposuresList
        },
        servicesDropdown: {
            availableValues: servicesList
        },
        kindServiceDropdown: {
            availableValues: servicesKindList
        },
        addressDropdown: {
            availableValues: addressList
        },
        addressComponentID: {
            addressVM: addressVM,
            visible: isNewAddress,
            handleModelChangeAddress: onUpdateModelAddressVM
        },
        createServiceButton: {
            enable: isComponentValid
        },
    };

    const resolvers = {
        resolveComponentMap: {
            addressComponent: AddressComponentAX
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onValidate: onValidate,
            handleExposureItem: handleExposureItem,
            handleServiceItem: handleServiceItem,
            handleKindServiceItem: handleKindServiceItem,
            handleAddressItem: handleAddressItem,
            createService: createService,
            cancelService: cancelService
        }
    };

    return (
        <>
            <ViewModelForm
                uiProps={metadata.claimData}
                model={claimSelected}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
            />
            <ViewModelForm
                uiProps={metadata.createService}
                model={serviceRequestVM}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
                onModelChange={handleModelChange}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </>
    );
}

export default withRouter(NewServiceCreateComponentAX);
